import { useState, useEffect } from 'react';
import WizardModal, { WizardLabel, WizardError } from './WizardModal';
import { get } from '../../api';

interface ReviewDay {
  date: string;
  weekday: string;
  planned_kcal: number;
  tracked_kcal: number;
  planned_protein_g: number;
  tracked_protein_g: number;
  meals_planned: number;
  meals_tracked: number;
  tracked: boolean;
}

interface ReviewSummary {
  avg_planned_kcal: number;
  avg_tracked_kcal: number;
  avg_planned_protein_g: number;
  avg_tracked_protein_g: number;
  tracking_compliance: number;
  days_tracked: number;
  days_total: number;
  best_day: string;
  worst_day: string;
}

interface ReviewData {
  week_start: string;
  days: ReviewDay[];
  summary: ReviewSummary;
  weight_change: { start_kg: number; end_kg: number; diff_kg: number } | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ReviewWizard({ open, onClose }: Props) {
  const [weekStart, setWeekStart] = useState(currentMonday());
  const [data, setData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    get<ReviewData>(`/review?week_start=${weekStart}`)
      .then(setData)
      .catch(() => setError('Review konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, weekStart]);

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d.toLocaleDateString('sv-SE'));
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d.toLocaleDateString('sv-SE'));
  };

  return (
    <WizardModal open={open} onClose={onClose} title="Wochen-Review" footer={null}>
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <button onClick={prevWeek} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-700">
          Woche ab {new Date(weekStart).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
        </span>
        <button onClick={nextWeek} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <span className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && <WizardError message={error} />}

      {data && !loading && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-2">
            <SummaryCard label="Tracking-Quote" value={`${Math.round(data.summary.tracking_compliance * 100)}%`} />
            <SummaryCard label="Tage getrackt" value={`${data.summary.days_tracked} / ${data.summary.days_total}`} />
            <SummaryCard label="Ø Kalorien" value={`${Math.round(data.summary.avg_tracked_kcal)} kcal`} sub={`Ziel: ${Math.round(data.summary.avg_planned_kcal)}`} />
            <SummaryCard label="Ø Protein" value={`${Math.round(data.summary.avg_tracked_protein_g)}g`} sub={`Ziel: ${Math.round(data.summary.avg_planned_protein_g)}g`} />
          </div>

          {/* Weight change */}
          {data.weight_change && (
            <div className="bg-gray-100 rounded-xl px-3 py-2">
              <WizardLabel>Gewicht</WizardLabel>
              <p className="text-sm text-gray-700 mt-0.5">
                {data.weight_change.start_kg} kg → {data.weight_change.end_kg} kg
                <span className={`ml-2 font-medium ${data.weight_change.diff_kg < 0 ? 'text-emerald-600' : data.weight_change.diff_kg > 0 ? 'text-orange-500' : 'text-gray-500'}`}>
                  ({data.weight_change.diff_kg > 0 ? '+' : ''}{data.weight_change.diff_kg.toFixed(1)} kg)
                </span>
              </p>
            </div>
          )}

          {/* Day-by-day */}
          <div>
            <WizardLabel>Tagesübersicht</WizardLabel>
            <div className="space-y-1.5 mt-1">
              {data.days.map(day => (
                <div key={day.date} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${day.tracked ? 'bg-white' : 'bg-gray-100/50'}`}>
                  <span className="text-xs font-medium text-gray-500 w-6">{day.weekday.slice(0, 2)}</span>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-400 rounded-full transition-all"
                        style={{ width: `${Math.min(100, day.planned_kcal > 0 ? (day.tracked_kcal / day.planned_kcal) * 100 : 0)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-500 w-20 text-right">
                    {day.tracked ? `${Math.round(day.tracked_kcal)} / ${Math.round(day.planned_kcal)}` : '–'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </WizardModal>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="text-base font-semibold text-gray-800">{value}</p>
      {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
    </div>
  );
}

function currentMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toLocaleDateString('sv-SE');
}
